import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import {
  Select, InputLabel, Input, MenuItem,
  FormControl, Chip, Card, Grid,
  CardContent, Button, InputBase,
  InputAdornment, Fab, IconButton,
  TextField, Typography, Popover
} from '@material-ui/core'

import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'

import { makeStyles, useTheme, fade } from '@material-ui/core/styles'

import { Toast as NotificationToast, ConfirmToast } from '../../tools'

import { ModalToast } from '../../tools/Toast'

import { getUserRoles, updateUserRole, postUserRole, deleteUserRole } from '../../../../redux/actions'

import { handleTextPrepper } from '../../../../utils'
import { statusTypes, statusPermissions } from '../../../../utils/variables'

const useStyles = makeStyles(theme => ({
  formControl: {
    // margin: theme.spacing(1),
    minWidth: 350,
    maxWidth: 350
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  chip: {
    margin: 2
  },
  noLabel: {
    marginTop: theme.spacing(3)
  },
  cssUnderline: {
    '&:after': {
      borderBottomColor: '#A4D164'
    }
  },
  cssLabel: {
    '&$cssFocused': {
      color: '#A4D164'
    }
  },
  cssFocused: {},
  roleCard: {
    width: '96%'
  },
  greenBtn: {
    color: '#fff',
    backgroundColor: '#A4D164',
    '&:hover': {
      backgroundColor: '#8AB651'
    }
  },
  deleteBtn: {
    backgroundColor: '#e57373',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#d05b5b'
    }
  },
  saveBtn: {
    backgroundColor: '#3984f7',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#0651C4'
    }
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: fade('#868787', 0.15),
    '&:hover': {
      backgroundColor: fade('#868787', 0.25)
    },
    width: '100%'
  },
  searchIcon: {
    padding: theme.spacing(0, 1),
    height: '100%',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none'
  },
  inputInput: {
    padding: theme.spacing(2, 1, 2, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(5)}px)`,
    transition: theme.transitions.create('width'),
    width: '100%'
  },
  searchBtn: {
    color: '#fff',
    backgroundColor: '#3984f7',
    '&:hover': {
      backgroundColor: '#0651C4'
    }
  },
  roleContainer: {
    maxHeight: '37em',
    width: '100%',
    padding: theme.spacing(2, 0),
    marginLeft: 'auto',
    marginRight: 'auto',
    // overflowX: 'hidden',
    overflowY: 'scroll'
  }
}))

const useTextStyles = makeStyles((theme) => ({
  root: {
    overflow: 'hidden',
    backgroundColor: '#fff',
    width: '100%',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    },
    '&$focused': {
      backgroundColor: '#f5f5f5 !important'
    }
  },
  focused: {}
}))

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
}

/**
 * Returns user roles forms
 */
const UserRolesList = ({ onSubmit, onSearch }) => {
  // hooks
  const dispatch = useDispatch()
  const classes = useStyles()
  const theme = useTheme()

  // redux
  const userRoles = useSelector(state => state.userRoles.userRoles)
  const userPermissions = useSelector(state => state.userListings.userPermissions)
  const userRoleSupervisors = useSelector(state => state.userListings.userRoleSupervisors)

  // local state
  const [rolesListFormSwap, setRolesListFormSwap] = useState('role-list')
  const [selectedRole, setSelectedRole] = useState({})
  const [newRoleName, setNewRoleName] = useState('')
  const [newRoleDesc, setNewRoleDesc] = useState('')

  const [rolesInfo, setRolesInfo] = useState([])
  const [adminRole, setAdminRole] = useState([])
  const [selectedAdminRole, setSelectedAdminRole] = useState([])
  const [permissions, setPermissions] = useState([])

  const [hasUnviewedPermission, setHasUnviewedPermission] = useState(false)
  const [updatedPermissions, setUpdatedPermissions] = useState([])
  const [permissionChangeAnchorEl, setPermissionChangeAnchorEl] = useState(null)

  const handlePermissionChangePopClose = () => {
    setPermissionChangeAnchorEl(null)
  }

  const handlePermissionChangePopOpen = (e, permissionIDs) => {
    setPermissionChangeAnchorEl(e.currentTarget)
    setUpdatedPermissions(permissionIDs)
  }

  const openPermissioChangeAnchorEl = Boolean(permissionChangeAnchorEl)

  const [searchVal, setSearchVal] = useState('')

  const getStyles = (name, adminRole, theme) => {
    return {
      fontWeight:
        adminRole.indexOf(name) === -1
          ? theme.typography.fontWeightRegular
          : theme.typography.fontWeightMedium
    }
  }

  const textClasses = useTextStyles()

  useEffect(() => {
    if (userRoles && userRoleSupervisors) {
      const userRolesAndSup = userRoles.map((role) => {
        const roleIndex = userRoleSupervisors.findIndex((supRole) => {
          return supRole.roleID === role.roleID
        })
        if (roleIndex !== -1) {
          const { roleSupervisors } = userRoleSupervisors[roleIndex]
          return {
            ...role,
            roleLevel: roleSupervisors.length
          }
        } else {
          return {
            ...role,
            roleLevel: 0
          }
        }
      })
      setRolesInfo(userRolesAndSup)
    }
  }, [userRoles, userRoleSupervisors])

  useEffect(() => {
    if (userPermissions) {
      setPermissions(userPermissions)
    }
  }, [userPermissions])

  const fireDeleteSuccess = () => {
    NotificationToast(false, 'Successfully Deleted Role!')
    dispatch(getUserRoles())
  }

  const fireDeleteFailure = () => {
    NotificationToast(true, 'Failed to Delete Role!')
  }

  const handleDeleteRole = async (roleID) => {
    const targetRole = rolesInfo.find((roleInfo) => roleInfo.roleID === roleID)
    if (targetRole) {
      if (targetRole.roleLevel !== 0) {
        ModalToast('error',
          `${targetRole.roleLevel} user${targetRole.roleLevel !== 1 ? 's have' : ' has'} this role!`, 'Please move users to another role before deleting this role.')
      } else {
        const deleteStat = await ConfirmToast({ msg: 'Are you sure you want to delete this role?' })
        if (deleteStat === true) {
          dispatch(deleteUserRole(roleID, fireDeleteSuccess, fireDeleteFailure))
          setRolesListFormSwap('role-list')
        }
      }
    }
  }

  const swapForms = () => {
    switch (rolesListFormSwap) {
      case 'role-list':
        return renderRolesList()
      case 'new-role':
        return renderNewRole()
      case 'update-role':
        return renderSelectedRole()
      default:
        return (<></>)
    }
  }

  const handleCreateNewRole = async (e) => {
    e.preventDefault()
    let error = false
    if (newRoleName === '') {
      NotificationToast(false, 'Must set a name to the role!', true)
      error = true
    }
    if (newRoleDesc === '') {
      NotificationToast(false, 'Must set a description to the role!', true)
      error = true
    }
    if (adminRole.length === 0) {
      NotificationToast(false, 'Must set at least one permission!', true)
      error = true
    }
    if (!error) {
      const permissionIDs = []
      adminRole.forEach((role) => {
        const permissionIndex = permissions.findIndex((permission) => {
          return permission.permissionDisplayName === role
        })
        if (permissionIndex !== -1) {
          permissionIDs.push(permissions[permissionIndex])
        }
      })
      if (permissionIDs.length !== 0) {
        const roleName = handleTextPrepper(newRoleName)
        const roleDesc = handleTextPrepper(newRoleDesc)
        dispatch(postUserRole(roleName, roleDesc, permissionIDs, firePostSuccess, firePostFailure))
        setNewRoleName('')
        setNewRoleDesc('')
        setAdminRole([])
        setRolesListFormSwap('role-list')
      }
    }
  }

  const firePostSuccess = () => {
    NotificationToast(false, 'Successfully Created New Role!')
    dispatch(getUserRoles())
  }

  const firePostFailure = () => {
    NotificationToast(true, 'Failed To Create New Role!')
  }

  const handleBackTrack = async (e) => {
    e.preventDefault()
    setRolesListFormSwap('role-list')
    setAdminRole([])
    setHasUnviewedPermission(false)
    setUpdatedPermissions([])
    handlePermissionChangePopClose()
  }

  const handleEditRole = (roleID) => {
    setRolesListFormSwap('update-role')
    const roleIndex = userRoles.findIndex((role) => {
      return role.roleID === roleID
    })
    const selectedAdminPermissions = userRoles[roleIndex].rolePermissions.map((permission) => {
      return (permission.permissionDisplayName)
    })

    const hasUnviewedPermission = userRoles[roleIndex].rolePermissions.findIndex(perm => perm.permissionKey === statusPermissions[statusTypes.UNVIEWED])

    if (hasUnviewedPermission !== -1) { setHasUnviewedPermission(true) }

    setSelectedRole(userRoles[roleIndex])
    setSelectedAdminRole(selectedAdminPermissions)
  }

  const handleConfirmUpdateRole = (permissionIDs) => {
    const roleID = selectedRole.roleID
    const roleName = handleTextPrepper(selectedRole.roleName)
    const roleDesc = handleTextPrepper(selectedRole.roleDesc)
    dispatch(updateUserRole(roleID, roleName, roleDesc, permissionIDs, fireUpdateSuccess, fireUpdateFailure))
    setRolesListFormSwap('role-list')
    setSelectedAdminRole([])
    setSelectedRole({})
    setHasUnviewedPermission(false)
    setUpdatedPermissions([])
    handlePermissionChangePopClose()
  }

  const handleUpdateRole = (e) => {
    e.preventDefault()

    let error = false
    if (selectedRole.roleName === '') {
      NotificationToast(false, 'Must set a name to the role!', true)
      error = true
    }
    if (selectedRole.roleDesc === '') {
      NotificationToast(false, 'Must set a description to the role!', true)
      error = true
    }
    if (selectedAdminRole.length === 0) {
      NotificationToast(false, 'Must set at least one permission!', true)
      error = true
    }
    if (!error) {
      const permissionIDs = []

      selectedAdminRole.forEach((role) => {
        const permissionIndex = permissions.findIndex((permission) => {
          return permission.permissionDisplayName === role
        })
        if (permissionIndex !== -1) {
          permissionIDs.push(permissions[permissionIndex])
        }
      })

      if (permissionIDs.length !== 0) {
        if (hasUnviewedPermission) {
          const removedUnviewedPermission = permissionIDs.findIndex(perm => perm.permissionKey === statusPermissions[statusTypes.UNVIEWED])
          if (removedUnviewedPermission === -1) {
            handlePermissionChangePopOpen(e, permissionIDs)
            return false
          }
        }
        handleConfirmUpdateRole(permissionIDs)
      }
    }
  }

  const fireUpdateSuccess = () => {
    NotificationToast(false, 'Successfully Updated Role!')
    dispatch(getUserRoles())
  }

  const fireUpdateFailure = () => {
    NotificationToast(true, 'Failed To Update Role!')
  }

  const isEnabled = newRoleName.length > 0 && adminRole.length > 0

  const renderNewRole = () => {
    return (
      <>
        <Grid item container direction='row' alignItems='center' justify='space-between'>
          <Grid item>
            <h2>New Role</h2>
          </Grid>

          <Grid item>
            <Button
              className={classes.greenBtn}
              onClick={handleBackTrack}
              variant='contained'
            >
              <span className='font-weight-bold'>Back</span>
            </Button>
          </Grid>
        </Grid>

        <Grid item container direction='row'>
          <Grid container item direction='column'>
            <Grid item>
              <Typography variant='h6'>Role Name</Typography>
            </Grid>

            <Grid item>
              <TextField
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder='Role Name'
                name='roleName'
                id='roleName'
                error={newRoleName.length === 0}
                required
                InputProps={{ classes: textClasses }}
                fullWidth
                variant='filled'
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item container direction='row'>
          <Grid item container direction='column'>
            <Grid item>
              <Typography variant='h6'>Role Description</Typography>
            </Grid>

            <Grid item>
              <TextField
                value={newRoleDesc}
                onChange={(e) => setNewRoleDesc(e.target.value)}
                placeholder='Role Description'
                name='roleDescription'
                id='roleDescription'
                // error={newRoleDesc.length === 0}
                // required
                InputProps={{ classes: textClasses }}
                fullWidth
                variant='filled'
                multiline
                rows='3'
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item container direction='row'>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id='multiple-roles-label' classes={{ root: classes.cssLabel, focused: classes.cssFocused }}>Permissions (Select one or more)</InputLabel>
              <Select
                labelId='multiple-roles-label'
                id='multiple-roles'
                multiple
                value={adminRole}
                className={classes.cssUnderline}
                onChange={e => setAdminRole(e.target.value)}
                input={<Input id='select-multiple-roles' />}
                error={adminRole.length === 0}
                required
                renderValue={selected => {
                  return (
                    <div className={classes.chips}>
                      {selected.map((value, i) => (
                        <Chip key={i} label={value} className={classes.chip} />
                      ))}
                    </div>
                  )
                }}
                MenuProps={MenuProps}
              >
                {permissions.map((permission, i) => (
                  <MenuItem
                    key={i}
                    value={permission.permissionDisplayName}
                    style={getStyles(permission, adminRole, theme)}
                  >
                    {permission.permissionDisplayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid item container direction='row'>
          <Grid item>
            <Button
              className={classes.saveBtn}
              color='primary'
              onClick={handleCreateNewRole}
              disabled={!isEnabled}
              variant='contained'
            >
              <span className='font-weight-bold'>Create Role</span>
            </Button>
          </Grid>
        </Grid>
      </>
    )
  }

  const renderSelectedRole = () => {
    return (
      <>
        <Grid item container direction='row' alignItems='center' justify='space-between'>
          <Grid item>
            <h2>Edit Role</h2>
          </Grid>

          <Grid item>
            <Button
              className={classes.greenBtn}
              onClick={handleBackTrack}
              variant='contained'
            >
              <span className='font-weight-bold'>Back</span>
            </Button>
          </Grid>
        </Grid>

        <Grid item container direction='row'>
          <Grid item container direction='column'>
            <Grid item>
              <Typography variant='h6'>Role Name</Typography>
            </Grid>

            <Grid item>
              <TextField
                value={selectedRole.roleName}
                onChange={(e) => setSelectedRole({ ...selectedRole, roleName: e.target.value })}
                placeholder='Role Name'
                name='roleName'
                id='roleName'
                InputProps={{ classes: textClasses }}
                fullWidth
                variant='filled'
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container item direction='row'>
          <Grid item container direction='column'>
            <Grid item>
              <Typography variant='h6'>Role Description</Typography>
            </Grid>

            <Grid item>
              <TextField
                value={selectedRole.roleDesc}
                onChange={(e) => setSelectedRole({ ...selectedRole, roleDesc: e.target.value })}
                placeholder='Role Description'
                name='roleDescription'
                id='roleDescription'
                InputProps={{ classes: textClasses }}
                fullWidth
                variant='filled'
                multiline
                rows='3'
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container item direction='row'>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id='multiple-roles-label' classes={{ root: classes.cssLabel, focused: classes.cssFocused }}>Permissions (Select one or more)</InputLabel>
              <Select
                labelId='multiple-roles-label'
                id='multiple-roles'
                multiple
                value={selectedAdminRole}
                className={classes.cssUnderline}
                onChange={e => setSelectedAdminRole(e.target.value)}
                input={<Input id='select-multiple-roles' />}
                renderValue={selected => (
                  <div className={classes.chips}>
                    {selected.map((value, i) => (
                      <Chip key={i} label={value} className={classes.chip} />
                    ))}
                  </div>
                )}
                MenuProps={MenuProps}
              >
                {permissions.map((permission, i) => (
                  <MenuItem key={i} value={permission.permissionDisplayName} style={getStyles(permission, adminRole, theme)}>
                    {permission.permissionDisplayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

          </Grid>
        </Grid>

        <Grid container item direction='row'>
          <Button
            className={classes.saveBtn}
            color='primary'
            onClick={handleUpdateRole}
            variant='contained'
          >
            <span className='font-weight-bold'>Update Role</span>
          </Button>
        </Grid>

        <Popover
          id={openPermissioChangeAnchorEl ? 'confirm-popover' : undefined}
          open={openPermissioChangeAnchorEl}
          anchorEl={permissionChangeAnchorEl}
          onClose={handlePermissionChangePopClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
        >
          <Grid container direction='column' style={{ padding: '1em' }}>
            <Typography
              style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#3984F7' }}
              gutterBottom
            >
              Heads Up!
            </Typography>

            <Typography
              style={{ fontSize: '1rem', fontWeight: 'bold' }}
              gutterBottom
            >
              By removing the "View Unviewed Captures" permission you will automatically remove access to any subscribed alerts for supervisors assigned to this role. Are you sure you wish to proceed?
            </Typography>

            <Grid
              item
              container
              direction='row'
              justify='flex-end'
              alignItems='center'
              style={{ marginTop: '.5em' }}
            >
              <Button
                style={{
                  marginRight: '.3em',
                  textTransform: 'none',
                  fontWeight: '600',
                  color: '#98B556'
                }}
                onClick={handlePermissionChangePopClose}
              >
                Cancel
              </Button>

              <Button
                style={{
                  textTransform: 'none',
                  fontWeight: '600',
                  color: 'white'
                }}
                color='primary'
                variant='contained'
                onClick={() => handleConfirmUpdateRole(updatedPermissions)}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </Popover>
      </>
    )
  }

  const renderRolesList = () => {
    return (
      <>
        <Grid container item direction='row' alignItems='center' justify='space-between'>
          <Grid item>
            <h2>Role Manager</h2>
          </Grid>

          <Grid item xs='auto'>
            <div className={classes.search}>
              <div className={classes.searchIcon}>
                <Fab
                  className={classes.searchBtn}
                  size='small'
                >
                  <SearchIcon />
                </Fab>
              </div>
              <InputBase
                placeholder='Role Name'
                classes={{
                  root: classes.inputRoot,
                  input: classes.inputInput
                }}
                inputProps={{ 'aria-label': 'search' }}
                onChange={(e) => {
                  setSearchVal(e.target.value)
                }}
                value={searchVal}
                endAdornment={
                  searchVal !== ''
                    ? (
                      <InputAdornment position='start'>
                        <IconButton onClick={() => setSearchVal('')}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>)
                    : (<></>)
                }
              />
            </div>
          </Grid>
        </Grid>

        <Grid container item direction='row' alignItems='center' justify='space-between'>
          <Grid item>
            <Typography variant='h6'>Account Roles</Typography>
          </Grid>

          <Grid item>
            <Button
              className={classes.greenBtn}
              onClick={(e) => { e.preventDefault(); setRolesListFormSwap('new-role') }}
              variant='contained'
            >
              <span className='font-weight-bold'>Create New Role</span>
            </Button>
          </Grid>
        </Grid>

        <Grid item container direction='row' justify='center' className={classes.roleContainer}>
          <Grid item container direction='column'>
            {rolesInfo.filter((roleInfo) => {
              return roleInfo.roleName.toLowerCase().includes(searchVal.toLowerCase())
            }).map((roleInfo, i) => (
              <Grid item container direction='row' justify='center' key={`role-${i}`} className='mx-auto my-2 w-98'>
                <Card raised className={classes.roleCard}>
                  <CardContent>
                    <Grid item container directin='row'>
                      <Grid item container direction='column' sm={8}>
                        <Grid item container direction='row'>
                          <h5 className='mt-2 mr-3' style={{ fontWeight: 'bold', color: '#007bff' }}>{roleInfo.roleName}</h5>
                          <span className='badge badge-primary special-badge'>{roleInfo.roleLevel}</span>
                        </Grid>
                        <Grid item container direction='row' className='mr-2 mt-2'>
                          <span>{roleInfo.roleDesc}</span>
                        </Grid>
                      </Grid>
                      <Grid item container direction='column' sm={4}>
                        <Grid item container direction='row' justify='flex-end'>
                          <Button
                            className={`${classes.greenBtn} mt-4 mr-3`}
                            onClick={(e) => { e.preventDefault(); handleEditRole(roleInfo.roleID) }}
                            variant='contained'
                          >
                            <span className='font-weight-bold'>Edit</span>
                          </Button>
                          <Button
                            className={`${classes.deleteBtn} mt-4 mr-3`}
                            onClick={(e) => { e.preventDefault(); handleDeleteRole(roleInfo.roleID) }}
                            variant='contained'
                          >
                            <span className='font-weight-bold'>Delete</span>
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </>
    )
  }

  return (
    <Grid container direction='row'>
      <Grid container item direction='column' spacing={2} className='mx-auto w-100'>
        {swapForms()}
      </Grid>
    </Grid>
  )
}

export default UserRolesList
